import { Skeleton, StatusHint, Tag } from '@indipia/ui-core';
import type { Recipient, RecipientType } from './types';
import { formatPhone } from './utils/phone';

const NAME_SUFFIX: Record<RecipientType, string> = {
  CUSTOMER: ' 고객님',
  PARTNER: ' 반장님',
  VENDOR: '',
};

interface RecipientCardProps {
  recipient: Recipient;
  onRemove: (key: string) => void;
  /** 담당 공종 Tag 의 제거(제외) — 담당 공종을 쓰는 화면에서만 전달 */
  onRemoveWorkType?: (key: string, workTypeCode: string) => void;
}

export function RecipientCard({ recipient, onRemove, onRemoveWorkType }: RecipientCardProps) {
  return (
    <div className="flex items-center gap-3 rounded-xl bg-surface-subtle px-3 py-2.5">
      {/* 아바타 */}
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-500 text-sm font-semibold text-white">
        {recipient.name.charAt(0)}
      </div>

      {/* 이름 · 연락처 */}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-text-primary">
          {recipient.name}{NAME_SUFFIX[recipient.recipientType]}
        </p>
        <p className="text-xs text-text-secondary">{formatPhone(recipient.mobile)}</p>
        {recipient.workTypesStatus === 'loading' && (
          <div className="mt-1"><Skeleton height={20} width={120} /></div>
        )}
        {recipient.workTypesStatus === 'error' && (
          <div className="mt-1"><StatusHint tone="error">담당 공종을 불러오지 못했습니다</StatusHint></div>
        )}
        {recipient.workTypesStatus === 'ready' && (recipient.workTypes?.length ?? 0) === 0 && (
          <div className="mt-1"><StatusHint tone="warning">보낼 공종이 없습니다</StatusHint></div>
        )}
        {recipient.workTypesStatus === 'ready' && (recipient.workTypes?.length ?? 0) > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {recipient.workTypes!.map((w) => (
              <Tag
                key={w.code}
                size="sm"
                onRemove={onRemoveWorkType ? () => onRemoveWorkType(recipient.key, w.code) : undefined}
              >
                {w.name}
              </Tag>
            ))}
          </div>
        )}
      </div>

      {/* 제거 버튼 */}
      <button
        type="button"
        onClick={() => onRemove(recipient.key)}
        className="flex shrink-0 items-center gap-1 rounded-lg border border-error px-2 py-1 text-xs text-error hover:bg-surface-subtle"
      >
        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
        </svg>
        제거
      </button>
    </div>
  );
}
